import React from 'react';
import ManageProspects from '../Features/ManageProspects';
import permissions from './configs/permissions';

const SignUp = React.lazy(() => import('../Features/Auth/SignUp'));

// new components
const LocationsAndEvents = React.lazy(() => import('../Features/LocationsAndEvents'));
const ManagePetitions = React.lazy(() => import('../Features/ManagePetitions'));
const GeneratePetitions = React.lazy(() => import('../Features/GeneratePetitions'));
const PetitionFieldReport = React.lazy(() => import('../Features/PetitionFieldReport'));
const EnterSignatures = React.lazy(() => import('../Features/EnterSignatures'));
const ReviewSignatures = React.lazy(() => import('../Features/ReviewSignatures'));
const ReviewPetitions = React.lazy(() => import('../Features/ReviewPetitions'));
const Settings = React.lazy(() => import('../Features/Settings'));
const CustomMethods = React.lazy(() => import('../Features/CustomMethods/List'));

const Initiatives = React.lazy(() => import('../Features/Initiative/Initiatives'));
const CreateInitiative = React.lazy(() => import('../Features/Initiative/Create'));
const EditInitiative = React.lazy(() => import('../Features/Initiative/Edit'));

const LandingPages = React.lazy(() => import('../Features/LandingPage/LandingPages'));
const CreateLandingPage = React.lazy(() => import('../Features/LandingPage/CreateLandingPage'));
const EditLandingPage = React.lazy(() => import('../Features/LandingPage/EditLandingPage'));

const LandingSuccessConfigurationList = React.lazy(() =>
  import('../Features/LandingSuccessConfiguration/List'),
);
const LandingSuccessConfigurationEdit = React.lazy(() =>
  import('../Features/LandingSuccessConfiguration/Edit'),
);

const Organizations = React.lazy(() => import('../Features/Organizations/List'));
const OrganizationsEdit = React.lazy(() => import('../Features/Organizations/Edit'));
const OrganizationCreate = React.lazy(() => import('../Features/Organizations/Create'));

const Users = React.lazy(() => import('../Features/Users/List'));
const UserEdit = React.lazy(() => import('../Features/Users/Edit'));
const InviteByEmail = React.lazy(() => import('../Features/Users/InviteByEmail'));
const CreateInviteLink = React.lazy(() => import('../Features/Users/CreateInviteLink'));
const AddWithForm = React.lazy(() => import('../Features/Users/AddWithForm'));

const JurisdictionsList = React.lazy(() => import('../Features/Jurisdictions/List'));
const JurisdictionCreate = React.lazy(() => import('../Features/Jurisdictions/Create'));
const JurisdictionEdit = React.lazy(() => import('../Features/Jurisdictions/Edit'));

const GlobalSettings = React.lazy(() => import('../Features/GlobalSettings'));

const Donations = React.lazy(() => import('../Features/Donations/List'));
const Donation = React.lazy(() => import('../Features/Donations/View'));

const DonateFormsList = React.lazy(() => import('../Features/DonateForms/List'));
const DonateFormCreate = React.lazy(() => import('../Features/DonateForms/Create'));
const DonateFormEdit = React.lazy(() => import('../Features/DonateForms/Edit'));

const LabelsList = React.lazy(() => import('../Features/Labels/List'));
const UserLabelsList = React.lazy(() => import('../Features/Labels/UserLabelsList'));
const CategoriesList = React.lazy(() => import('../Features/Categories/List'));
const RegistrationForms = React.lazy(() =>
  import('../Features/RegistrationForm/RegistrationForms'),
);
const CreateRegistrationForm = React.lazy(() =>
  import('../Features/RegistrationForm/CreateRegistrationForm'),
);
const EditRegistrationForm = React.lazy(() =>
  import('../Features/RegistrationForm/EditRegistrationForm'),
);
const Manual = React.lazy(() => import('../Features/Manual'));
const SettingsPage = React.lazy(() => import('../Features/SettingsPage'));

const WalkRoutes = React.lazy(() => import('../Features/WalkRoute/List'));
const WalkRouteView = React.lazy(() => import('../Features/WalkRoute/View'));

const RecruitsPage = React.lazy(() => import('../Features/Recruits/RecruitsPage'));

const BatchScanAndUpload = React.lazy(() => import('../Features/BatchScanAndUpload'));
const UploadAndScan = React.lazy(() => import('../Features/UploadAndScan'));
const Reports = React.lazy(() => import('../Features/Reports'));

const ViewPetitionImages = React.lazy(() => import('../Features/ViewPetitionImages'));
const PetitionImages = React.lazy(() => import('../Features/ViewPetitionImages/PetitionImages'));

const routes = [
  { path: '/', exact: true, name: 'Home' },
  {
    path: '/manage-petitions',
    name: 'Assign Petitions',
    component: ManagePetitions,
    permission: permissions.view_assign_petitions,
    initiativeMode: true,
    initiativeSettingKey: 'assign_petitions',
  },
  {
    path: '/generate-petitions',
    name: 'Generate Petitions',
    component: GeneratePetitions,
    permission: permissions.view_generate_petitions,
    initiativeMode: true,
    initiativeSettingKey: 'generate_petitions',
  },
  {
    path: '/petition-field-report',
    name: 'Petition Field Report',
    component: PetitionFieldReport,
    permission: permissions.create_field_report,
    initiativeMode: true,
    adminMode: true,
    isWithoutInitiative: true,
  },
  {
    path: '/review-signatures',
    name: 'Petition Review',
    component: ReviewSignatures,
    permission: permissions.view_review_petitions,
    initiativeMode: true,
    initiativeSettingKey: 'review_signatures',
  },
  {
    path: '/manage-prospects',
    name: 'Manage Prospects',
    component: ManageProspects,
    initiativeMode: true,
    adminMode: true,
  },
  {
    path: '/upload-and-scan',
    name: 'Upload & Scan',
    component: UploadAndScan,
    permission: permissions.read_uploaded_files,
    initiativeMode: true,
    adminMode: true,
  },
  {
    path: '/reports',
    name: 'Reports',
    component: Reports,
    permission: permissions.view_reports,
    initiativeMode: true,
    initiativeSettingKey: 'reports_enabled',
  },
  {
    path: '/enter-signatures',
    name: 'Enter Signatures',
    component: EnterSignatures,
    permission: permissions.view_enter_signature_data,
    initiativeMode: true,
    initiativeSettingKey: 'enter_signature',
  },
  {
    path: '/review-petitions',
    name: 'Review Petitions',
    component: ReviewPetitions,
    permission: permissions.view_review_petitions,
    initiativeMode: true,
    initiativeSettingKey: 'review_petitions_enabled',
  },
  {
    path: '/audit-queue',
    name: 'Review Audits',
    component: ReviewPetitions,
    permission: permissions.read_audit,
    initiativeMode: true,
    initiativeSettingKey: 'audit_queue_enabled',
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Settings,
    permission: permissions.view_settings,
    initiativeMode: true,
  },
  {
    path: '/custom-methods',
    name: 'Custom Methods',
    component: CustomMethods,
    permission: permissions.view_settings,
    initiativeMode: true,
  },
  {
    path: '/initiatives',
    exact: true,
    name: 'Initiatives',
    component: Initiatives,
    permission: permissions.view_initiatives,
    adminMode: true,
  },
  {
    path: '/initiative/create',
    name: 'Create Initiative',
    component: CreateInitiative,
    permission: permissions.create_initiative,
    adminMode: true,
  },
  {
    path: '/initiative/edit/:id',
    name: 'Edit Initiative',
    component: EditInitiative,
    permission: permissions.update_initiative,
    adminMode: true,
  },
  {
    path: '/landing-pages',
    exact: true,
    name: 'Landing Pages',
    component: LandingPages,
    permission: permissions.view_landing_pages,
    initiativeMode: true,
    initiativeSettingKey: 'landing_pages',
  },
  {
    path: '/donations',
    name: 'Donations',
    component: Donations,
    permission: permissions.view_donations,
    initiativeMode: true,
  },
  {
    path: '/donation/:id',
    name: 'Donation',
    component: Donation,
    permission: permissions.view_donations,
    initiativeMode: true,
  },
  {
    path: '/donate-forms',
    name: 'Donate forms',
    component: DonateFormsList,
    permission: permissions.update_landing_page,
    adminMode: true,
  },
  {
    path: '/donate-form/create',
    name: 'Donate form create',
    component: DonateFormCreate,
    permission: permissions.update_landing_page,
    adminMode: true,
  },
  {
    path: '/donate-form/edit/:id',
    name: 'Donate form edit',
    component: DonateFormEdit,
    permission: permissions.update_landing_page,
    adminMode: true,
  },
  {
    path: '/landing-page/create',
    name: 'Create Landing Page',
    component: CreateLandingPage,
    permission: permissions.create_landing_page,
    initiativeMode: true,
    initiativeSettingKey: 'landing_pages',
  },
  {
    path: '/landing-page/edit/:id',
    name: 'Edit Landing Page',
    component: EditLandingPage,
    permission: permissions.update_landing_page,
    initiativeMode: true,
    initiativeSettingKey: 'landing_pages',
  },
  {
    path: '/landing-page/:landingid/success',
    exact: true,
    name: 'Success configuration',
    component: LandingSuccessConfigurationList,
    permission: permissions.view_landing_pages,
    initiativeMode: true,
  },
  {
    path: '/landing-page/:landingid/success/:id',
    exact: true,
    name: 'Success configuration edit',
    component: LandingSuccessConfigurationEdit,
    permission: permissions.view_landing_pages,
    initiativeMode: true,
  },
  {
    path: '/organizations',
    name: 'Organizations',
    component: Organizations,
    permission: permissions.view_organizations,
    adminMode: true,
  },
  {
    path: '/organization/edit/:id',
    name: 'Organization edit',
    component: OrganizationsEdit,
    permission: permissions.update_organization,
    adminMode: true,
  },
  {
    path: '/organization/create/',
    name: 'Organization create',
    component: OrganizationCreate,
    permission: permissions.create_organization,
    adminMode: true,
  },
  {
    path: '/locations-events',
    name: 'Locations & Events',
    component: LocationsAndEvents,
    permission: permissions.view_users,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/users',
    name: 'Users',
    component: Users,
    permission: permissions.view_users,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/user/edit/:id',
    name: 'User edit',
    component: UserEdit,
    permission: permissions.read_user,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/user/invite-by-email',
    name: 'Invite by email',
    component: InviteByEmail,
    permission: permissions.invite_user_to_initiative,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/user/create-invite-link',
    name: 'Create invite link',
    component: CreateInviteLink,
    permission: permissions.invite_user_to_initiative,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/user/add-with-form',
    name: 'Add with form',
    component: AddWithForm,
    permission: permissions.invite_user_to_initiative,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/jurisdictions',
    name: 'Jurisdictions',
    component: JurisdictionsList,
    permission: permissions.view_jurisdictions,
    adminMode: true,
  },
  {
    path: '/global-settings',
    name: 'Global Settings',
    component: GlobalSettings,
    permission: permissions.view_global_settings,
    adminMode: true,
  },
  {
    path: '/jurisdiction/create',
    name: 'Jurisdiction create',
    component: JurisdictionCreate,
    permission: permissions.create_jurisdiction,
    adminMode: true,
  },
  {
    path: '/jurisdiction/edit/:id',
    name: 'Jurisdiction edit',
    component: JurisdictionEdit,
    permission: permissions.update_jurisdiction,
    adminMode: true,
  },
  {
    path: '/labels',
    name: 'Labels',
    component: LabelsList,
    permission: permissions.create_label,
    initiativeMode: true,
  },
  {
    path: '/user-labels',
    name: 'User Labels',
    component: UserLabelsList,
    permission: permissions.create_label,
    initiativeMode: true,
  },
  {
    path: '/categories',
    name: 'Categories',
    component: CategoriesList,
    permission: permissions.create_category,
    initiativeMode: true,
  },
  {
    path: '/registration-forms',
    name: 'Registration Forms',
    component: RegistrationForms,
    initiativeMode: true,
  },
  {
    path: '/registration-form/create',
    name: 'Create Registration Form',
    component: CreateRegistrationForm,
    initiativeMode: true,
  },
  {
    path: '/registration-form/edit/:id',
    name: 'Edit Registration Form',
    component: EditRegistrationForm,
    initiativeMode: true,
  },
  {
    path: '/manual',
    name: 'Manual',
    component: Manual,
    initiativeMode: true,
    adminMode: true,
  },
  {
    path: '/auth/sign-up/:id/:token',
    name: 'Sign up',
    component: SignUp,
    adminMode: true,
    initiativeMode: true,
    isWithoutInitiative: true,
  },
  {
    path: '/settings-page',
    name: 'Setting Page',
    component: SettingsPage,
    adminMode: true,
    initiativeMode: true,
    isWithoutInitiative: true,
  },
  {
    path: '/recruits',
    name: 'Recruits',
    component: RecruitsPage,
    adminMode: true,
    initiativeMode: true,
  },
  {
    path: '/canvas-routes',
    name: 'Canvass Routes',
    component: WalkRoutes,
    initiativeMode: true,
  },
  {
    path: '/qa-petitions',
    name: 'QA Petitions',
    component: ReviewPetitions,
    initiativeMode: true,
  },
  {
    path: '/walk-route/view/:id',
    name: 'Canvass Route view',
    component: WalkRouteView,
    initiativeMode: true,
  },
  {
    path: '/batch-scan-and-upload',
    name: 'Batch Scan & Upload',
    component: BatchScanAndUpload,
    initiativeMode: true,
  },
  {
    path: '/viewer',
    name: 'Petition Images',
    component: ViewPetitionImages,
    permission: permissions.view_petition_images,
    initiativeMode: true,
    exact: true,
  },
  {
    path: '/viewer/:id',
    name: 'Petition Images',
    component: PetitionImages,
    // permission: permissions.view_petition_images,
    initiativeMode: true,
    isWithoutInitiative: true,
  },
];

export default routes;
